import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import usePermissions from '../../auth/usePermissions';
import ConfirmModal from '../../components/utils/ConfirmModal';
import { authFetch } from '../../components/utils/authFetch';
import notify from '../../utils/notify';
import '../../component-styles/RecordPages.css';
import API_BASE from '../../utils/api.js';

function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const ALLOWED_CONTENT_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/avif',
  'image/svg+xml',
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

export default function FilesSection() {
  const { projectId } = useParams();
  const userId = localStorage.getItem('userId');
  const outlet = useOutletContext?.() || {};
  const project = outlet.project;
  const setProject = outlet.setProject;

  const { permissions } = usePermissions();
  const canEdit = permissions.createProductsServices !== false;

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef = useRef(null);

  const fetchFiles = useCallback(async () => {
    if (!userId || !projectId) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/project-files/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`,
        { cache: 'no-store' }
      );
      const data = await res.json();
      setFiles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch project files:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, projectId]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // Files uploaded on the New/Edit Project form live on the project record
  // itself (project.documents = [{ name, url }]) rather than in ProjectFiles.
  const intakeDocs = useMemo(
    () =>
      (Array.isArray(project?.documents) ? project.documents : []).map((d) => ({
        key: `intake:${d.url || d.name}`,
        name: d.name,
        url: d.url,
        size: 0,
        uploadedAt: '',
        source: 'intake',
      })),
    [project?.documents]
  );

  const uploadedDocs = useMemo(
    () =>
      files.map((f) => ({
        key: `upload:${f.sk}`,
        name: f.name,
        url: f.url,
        size: f.size,
        uploadedAt: f.uploadedAt,
        source: 'upload',
        sk: f.sk,
      })),
    [files]
  );

  const allFiles = useMemo(
    () =>
      [...uploadedDocs, ...intakeDocs].sort((a, b) =>
        (b.uploadedAt || '').localeCompare(a.uploadedAt || '')
      ),
    [uploadedDocs, intakeDocs]
  );

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const contentType = file.type || 'application/octet-stream';
    if (!ALLOWED_CONTENT_TYPES.has(contentType)) {
      notify.error('That file type is not supported.');
      return;
    }

    try {
      setUploading(true);
      const signRes = await authFetch(
        `${API_BASE}/api/upload/presigned-url?filename=${encodeURIComponent(
          file.name
        )}&type=${encodeURIComponent(contentType)}&size=${encodeURIComponent(file.size)}`
      );
      const sign = await signRes.json();
      if (!signRes.ok) throw new Error(sign.error || 'Failed to get upload URL');

      const uploadRes = await fetch(sign.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('Upload failed');

      const createRes = await fetch(
        `${API_BASE}/api/project-files/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: file.name,
            url: sign.fileUrl,
            size: file.size,
            contentType,
          }),
        }
      );
      const created = await createRes.json();
      if (!createRes.ok) throw new Error(created.error || 'Failed to save file');

      setFiles((prev) => [created, ...prev]);
    } catch (err) {
      console.error('File upload error:', err);
      notify.error(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  }

  async function handleDeleteUploaded(f) {
    setFiles((prev) => prev.filter((x) => x.sk !== f.sk));
    try {
      await fetch(
        `${API_BASE}/api/project-files/${encodeURIComponent(userId)}/${encodeURIComponent(f.sk)}`,
        { method: 'DELETE' }
      );
    } catch (err) {
      console.error('Failed to delete file:', err);
      notify.error('Failed to delete file');
      fetchFiles();
    }
  }

  async function handleDeleteIntake(doc) {
    const prevDocs = Array.isArray(project?.documents) ? project.documents : [];
    const nextDocs = prevDocs.filter((d) => d.url !== doc.url);
    setProject?.((prev) => ({ ...(prev || {}), documents: nextDocs }));
    try {
      const res = await fetch(
        `${API_BASE}/api/projects/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ documents: nextDocs }),
        }
      );
      if (!res.ok) throw new Error('Failed to remove file');
    } catch (err) {
      console.error('Failed to delete project document:', err);
      notify.error('Failed to delete file');
      setProject?.((prev) => ({ ...(prev || {}), documents: prevDocs }));
    }
  }

  function handleDelete(entry) {
    if (entry.source === 'intake') return handleDeleteIntake(entry);
    return handleDeleteUploaded(entry);
  }

  return (
    <section className="record-section">
      <div className="record-top-row">
        <div className="record-header-bar">
          <h2 className="record-title">Files</h2>
        </div>
        <p className="record-subtext">
          Contracts, floor plans, photos, and other documents for this project — including
          anything uploaded when the project was created.
        </p>

        {canEdit && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <button
              className="record-add-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading…' : '+ Add File'}
            </button>
          </div>
        )}
      </div>

      <div className="record-card">
        <div className="record-table-wrap">
          {loading ? (
            <div className="record-empty">Loading files…</div>
          ) : allFiles.length === 0 ? (
            <div className="record-empty">No files yet. Add a file to get started.</div>
          ) : (
            <table className="record-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allFiles.map((f) => (
                  <tr key={f.key}>
                    <td>
                      <a href={f.url} target="_blank" rel="noreferrer">
                        {f.name}
                      </a>
                    </td>
                    <td>{formatFileSize(f.size)}</td>
                    <td>
                      {f.uploadedAt ? f.uploadedAt.slice(0, 10) : 'At project setup'}
                    </td>
                    <td>
                      {canEdit && (
                        <button className="link-btn" onClick={() => setDeleteTarget(f)}>
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        title="Delete File"
        message={`Delete "${deleteTarget?.name || 'this file'}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          handleDelete(deleteTarget);
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </section>
  );
}
